import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchProductVariant,
  fetchProductVariants,
  type ProductVariant,
} from '../../api/productVariants';
import {
  addProductRelatedVariant,
  fetchProductRelatedVariants,
  removeProductRelatedVariant,
  type ProductRelatedVariant,
} from '../../api/productRelatedVariants';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

const VariantRelatedProductsPage: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const [variant, setVariant] = useState<ProductVariant | null>(null);
  const [variantError, setVariantError] = useState<string | null>(null);

  const [related, setRelated] = useState<ProductRelatedVariant[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<ProductVariant[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedVariantIds, setSelectedVariantIds] = useState<string[]>([]);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!id) {
      return;
    }
    setVariant(null);
    setVariantError(null);
    fetchProductVariant(id)
      .then(setVariant)
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Unable to load variant.';
        setVariantError(message);
      });
  }, [id]);

  useEffect(() => {
    if (!id) {
      return;
    }
    setRelatedLoading(true);
    setRelatedError(null);
    fetchProductRelatedVariants(id)
      .then(setRelated)
      .catch((err) => {
        const message = err instanceof Error ? err.message : 'Failed to load related variants.';
        setRelatedError(message);
      })
      .finally(() => setRelatedLoading(false));
  }, [id, refreshCounter]);

  const existingVariantIds = useMemo(
    () => new Set(related.map((item) => item.relatedVariant.id)),
    [related],
  );

  const handleSearch = async () => {
    if (!search.trim()) {
      setSearchResults([]);
      setSearchError('Enter a search term.');
      return;
    }

    setSearchError(null);
    setSearchLoading(true);

    try {
      const response = await fetchProductVariants({ search: search.trim(), limit: 20, page: 1 });
      setSearchResults(response.data);
      setSelectedVariantIds([]);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to search variants.';
      setSearchError(message);
    } finally {
      setSearchLoading(false);
    }
  };

  const toggleVariantSelection = (variantId: string) => {
    setSelectedVariantIds((prev) =>
      prev.includes(variantId) ? prev.filter((id) => id !== variantId) : [...prev, variantId],
    );
  };

  const handleBulkAdd = async () => {
    if (!id || selectedVariantIds.length === 0) {
      return;
    }

    setLinking(true);
    setSearchError(null);

    try {
      const uniqueIds = Array.from(new Set(selectedVariantIds));
      for (const relatedId of uniqueIds) {
        if (existingVariantIds.has(relatedId) || relatedId === id) {
          continue;
        }
        await addProductRelatedVariant(id, relatedId);
      }

      setSelectedVariantIds([]);
      setSearchResults([]);
      setSearch('');
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to add related variants.';
      setSearchError(message);
    } finally {
      setLinking(false);
    }
  };

  const handleRemove = async (relationId: string) => {
    if (!id) {
      return;
    }
    if (!window.confirm('Remove this related variant?')) {
      return;
    }
    try {
      await removeProductRelatedVariant(id, relationId);
      setRefreshCounter((prev) => prev + 1);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to remove related variant.';
      setRelatedError(message);
    }
  };

  if (!id) {
    return (
      <div className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
        <p className="text-sm text-danger">Invalid related variants route.</p>
      </div>
    );
  }

  const title = variant ? `Variant – ${variant.name}` : 'Variant related products';

  return (
    <div className="rounded-2xl bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <PageHeaderWithBack
        title={title}
        description={
          <p className="mt-2 max-w-2xl text-sm text-slate-500">
            Link frequently recommended products or services so they appear as related options when building orders
            that include this variant.
          </p>
        }
        onBack={() => navigate('/inventory/variants')}
        backLabel="Back to variants"
      />

      {variantError ? <p className="mt-4 text-sm text-danger">{variantError}</p> : null}

      <section className="mt-6 space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-inner">
          <h2 className="text-sm font-semibold text-slate-700">Add related variants</h2>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search product or service variants..."
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-72"
              disabled={searchLoading}
            />
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
              onClick={handleSearch}
              disabled={searchLoading}
            >
              {searchLoading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchError ? <p className="mt-2 text-sm text-danger">{searchError}</p> : null}

          {searchResults.length > 0 ? (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-center">Select</th>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">ERP ID</th>
                    <th className="px-4 py-3 font-semibold">Article</th>
                    <th className="px-4 py-3 font-semibold">Brand</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((v) => {
                    const disabled = existingVariantIds.has(v.id) || v.id === id;
                    const checked = selectedVariantIds.includes(v.id);
                    return (
                      <tr key={v.id} className="even:bg-slate-50/60">
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                            checked={checked || disabled}
                            onChange={() => toggleVariantSelection(v.id)}
                            disabled={disabled || linking}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-700">{v.name}</td>
                        <td className="px-4 py-3 text-slate-500">
                          {v.isCreatedByWorkshopManagement ? 'Service' : 'Product'}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{v.erpId}</td>
                        <td className="px-4 py-3 text-slate-500">{v.articleNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{v.brand?.name ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
                <span className="text-xs text-slate-500">
                  {selectedVariantIds.length} variant{selectedVariantIds.length === 1 ? '' : 's'} selected
                </span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                  onClick={handleBulkAdd}
                  disabled={linking || selectedVariantIds.length === 0}
                >
                  {linking ? 'Linking…' : 'Link selected'}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-inner">
          <h2 className="text-sm font-semibold text-slate-700">Linked variants</h2>
          {relatedError ? (
            <p className="mt-2 text-sm text-danger">{relatedError}</p>
          ) : null}
          {relatedLoading ? (
            <p className="mt-4 text-sm text-slate-400">Loading related variants…</p>
          ) : related.length === 0 ? (
            <p className="mt-4 text-sm text-slate-400">No related variants linked yet.</p>
          ) : (
            <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
              <table className="min-w-full text-left text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Name</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">ERP ID</th>
                    <th className="px-4 py-3 font-semibold">Article</th>
                    <th className="px-4 py-3 font-semibold">Brand</th>
                    <th className="px-4 py-3 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {related.map((relation) => {
                    const v = relation.relatedVariant;
                    return (
                      <tr key={relation.id} className="even:bg-slate-50/60">
                        <td className="px-4 py-3 font-medium text-slate-700">{v.name}</td>
                        <td className="px-4 py-3 text-slate-500">
                          {v.isCreatedByWorkshopManagement ? 'Service' : 'Product'}
                        </td>
                        <td className="px-4 py-3 text-slate-500">{v.erpId}</td>
                        <td className="px-4 py-3 text-slate-500">{v.articleNumber ?? '—'}</td>
                        <td className="px-4 py-3 text-slate-500">{v.brand?.name ?? '—'}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-lg border border-danger/40 px-3 py-1.5 text-xs font-semibold text-danger transition hover:bg-danger/10"
                            onClick={() => handleRemove(relation.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default VariantRelatedProductsPage;
